import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
  inject
} from '@angular/core';
import { BodyPortalDirective } from '@shared/directives/body-portal.directive';
import {
  BARCODE_SCAN_INTERVAL_MS,
  RetailBarcodeDetector,
  barcodeVideoConstraints,
  createRetailBarcodeDetector,
  createRetailBarcodeReader,
  drawBarcodeBand,
  nextBarcodeCandidate,
  readRetailBarcode,
  setBarcodeTorch,
  tuneBarcodeTrack
} from '@shared/services/barcode-camera';

export interface BarcodeScanCapture {
  code: string;
  frameJpeg: string | null;
}

@Component({
  selector: 'app-barcode-scanner-modal',
  standalone: true,
  imports: [CommonModule, BodyPortalDirective],
  templateUrl: './barcode-scanner-modal.component.html',
  styleUrl: './barcode-scanner-modal.component.css'
})
export class BarcodeScannerModalComponent implements OnChanges, OnDestroy {
  private readonly zone = inject(NgZone);

  @Input() isOpen = false;
  @Input() savePackagePhoto = false;
  @Output() close = new EventEmitter<void>();
  @Output() scanCode = new EventEmitter<BarcodeScanCapture>();
  @Output() scanErrorEvent = new EventEmitter<string>();

  @ViewChild('scanVideo') scanVideo?: ElementRef<HTMLVideoElement>;

  scanStarting = false;
  scanSuccess = false;
  scanSuccessCode: string | null = null;
  scanFreezeUrl: string | null = null;
  scanError: string | null = null;
  scanTorchAvailable = false;
  scanTorchOn = false;

  private barcodeReader: ReturnType<typeof createRetailBarcodeReader> | null = null;
  private barcodeDetector: RetailBarcodeDetector | null = null;
  private scanStream: MediaStream | null = null;
  private scanVideoTrack: MediaStreamTrack | null = null;
  private scanCanvas: HTMLCanvasElement | null = null;
  private scanLoopTimer: ReturnType<typeof setTimeout> | null = null;
  private scanLoopActive = false;
  private scanSession = 0;
  private pendingBarcode: string | null = null;
  private scanSuccessTimer: ReturnType<typeof setTimeout> | null = null;
  private scanAudioCtx: AudioContext | null = null;

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    if (this.isOpen && !this.scanSuccess) {
      this.stopScan();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        void this.startScan();
      } else {
        this.cleanupScan();
      }
    }
  }

  ngOnDestroy(): void {
    this.cleanupScan();
    void this.scanAudioCtx?.close();
    this.scanAudioCtx = null;
  }

  async startScan(): Promise<void> {
    if (this.scanStarting) return;

    const session = ++this.scanSession;
    this.pendingBarcode = null;
    this.scanError = null;
    this.scanSuccess = false;
    this.scanSuccessCode = null;
    this.scanFreezeUrl = null;
    this.scanTorchAvailable = false;
    this.scanTorchOn = false;
    this.scanStarting = true;

    try {
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
      if (!this.isScanSession(session)) return;

      const video = this.scanVideo?.nativeElement;
      if (!video) throw new Error('missing-video');

      const detectorPromise = createRetailBarcodeDetector();
      const stream = await navigator.mediaDevices.getUserMedia(barcodeVideoConstraints());
      if (!this.isScanSession(session)) {
        this.stopStream(stream);
        return;
      }

      this.scanStream = stream;
      const track = stream.getVideoTracks()[0] ?? null;
      this.scanVideoTrack = track;
      video.srcObject = stream;
      video.muted = true;
      await video.play();

      if (!this.isScanSession(session)) {
        this.releaseScanCamera();
        return;
      }

      if (track) {
        const tuned = await tuneBarcodeTrack(track);
        if (!this.isScanSession(session)) {
          this.releaseScanCamera();
          return;
        }
        this.scanTorchAvailable = tuned.torch;
      }

      this.barcodeDetector = await detectorPromise;
      if (!this.barcodeDetector) {
        this.barcodeReader = createRetailBarcodeReader();
      }
      if (!this.isScanSession(session)) {
        this.releaseScanCamera();
        return;
      }

      this.scanLoopActive = true;
      this.zone.runOutsideAngular(() => this.queueScanTick(session, 0));
    } catch {
      if (this.isScanSession(session)) {
        this.releaseScanCamera();
        this.scanError = null;
        this.scanErrorEvent.emit('לא ניתן לפתוח את המצלמה. בדקו הרשאות או הזינו ברקוד ידנית.');
        this.close.emit();
      }
    } finally {
      if (this.isScanSession(session)) {
        this.scanStarting = false;
      }
    }
  }

  stopScan(): void {
    this.cleanupScan();
    this.close.emit();
  }

  private cleanupScan(): void {
    this.scanSession++;
    this.pendingBarcode = null;
    if (this.scanSuccessTimer != null) {
      clearTimeout(this.scanSuccessTimer);
      this.scanSuccessTimer = null;
    }
    this.releaseScanCamera();
    this.scanStarting = false;
    this.scanSuccess = false;
    this.scanSuccessCode = null;
    this.scanFreezeUrl = null;
    this.scanError = null;
  }

  async refocusScan(): Promise<void> {
    const track = this.scanVideoTrack;
    if (!track || this.scanSuccess || !this.isOpen) return;
    await tuneBarcodeTrack(track);
  }

  async toggleTorch(): Promise<void> {
    const track = this.scanVideoTrack;
    if (!track || !this.scanTorchAvailable || this.scanSuccess) return;

    const next = !this.scanTorchOn;
    const applied = await setBarcodeTorch(track, next);
    if (applied) {
      this.scanTorchOn = next;
    }
  }

  private queueScanTick(session: number, delayMs: number): void {
    if (!this.isScanSession(session) || !this.scanLoopActive) return;

    this.scanLoopTimer = setTimeout(() => {
      this.scanLoopTimer = null;
      void this.scanTick(session);
    }, delayMs);
  }

  private async scanTick(session: number): Promise<void> {
    if (!this.isScanSession(session) || !this.scanLoopActive || this.scanSuccess) return;

    const started = performance.now();
    const video = this.scanVideo?.nativeElement;
    let code: string | null = null;
    if (video && video.videoWidth > 0) {
      const canvas = this.ensureScanCanvas();
      if (drawBarcodeBand(video, canvas)) {
        code = await readRetailBarcode(canvas, this.barcodeDetector, this.barcodeReader);
      }
    }

    if (!this.isScanSession(session) || !this.scanLoopActive || this.scanSuccess) return;

    const step = nextBarcodeCandidate(this.pendingBarcode, code);
    this.pendingBarcode = step.pending;
    if (step.confirmed) {
      const confirmed = step.confirmed;
      this.zone.run(() => this.playScanSuccess(confirmed));
      return;
    }

    const elapsed = performance.now() - started;
    this.queueScanTick(session, Math.max(0, BARCODE_SCAN_INTERVAL_MS - elapsed));
  }

  private playScanSuccess(code: string): void {
    if (this.scanSuccess) return;

    const video = this.scanVideo?.nativeElement;
    this.scanFreezeUrl = video ? this.captureScanFrame(video) : null;
    this.scanSuccess = true;
    this.scanSuccessCode = code;
    this.playScanSuccessSound();

    this.releaseScanCamera();

    if (this.scanSuccessTimer != null) {
      clearTimeout(this.scanSuccessTimer);
    }
    this.scanSuccessTimer = setTimeout(() => {
      this.scanSuccessTimer = null;
      const frameJpeg = this.scanFreezeUrl;
      this.scanSuccess = false;
      this.scanSuccessCode = null;
      this.scanFreezeUrl = null;
      this.scanCode.emit({ code, frameJpeg });
    }, 1100);
  }

  private ensureScanCanvas(): HTMLCanvasElement {
    if (!this.scanCanvas) {
      this.scanCanvas = document.createElement('canvas');
    }
    return this.scanCanvas;
  }

  private isScanSession(session: number): boolean {
    return session === this.scanSession;
  }

  private releaseScanCamera(): void {
    this.scanLoopActive = false;
    if (this.scanLoopTimer != null) {
      clearTimeout(this.scanLoopTimer);
      this.scanLoopTimer = null;
    }

    const video = this.scanVideo?.nativeElement;
    if (video) {
      try {
        video.pause();
      } catch {
        // ignore
      }
      video.srcObject = null;
    }

    if (this.scanStream) {
      this.stopStream(this.scanStream);
    }
    this.scanStream = null;
    this.scanVideoTrack = null;
    this.barcodeReader = null;
    this.barcodeDetector = null;
    this.scanCanvas = null;
    this.scanTorchAvailable = false;
    this.scanTorchOn = false;
  }

  private stopStream(stream: MediaStream): void {
    for (const track of stream.getTracks()) {
      try {
        track.stop();
      } catch {
        // ignore
      }
    }
  }

  private captureScanFrame(video: HTMLVideoElement): string | null {
    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) return null;

    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      ctx.drawImage(video, 0, 0, width, height);
      return canvas.toDataURL('image/jpeg', 0.82);
    } catch {
      return null;
    }
  }

  private playScanSuccessSound(): void {
    try {
      const AudioCtx =
        globalThis.AudioContext ||
        (globalThis as unknown as { webkitAudioContext?: typeof AudioContext })
          .webkitAudioContext;
      if (!AudioCtx) return;

      if (!this.scanAudioCtx || this.scanAudioCtx.state === 'closed') {
        this.scanAudioCtx = new AudioCtx();
      }
      const ctx = this.scanAudioCtx;
      void ctx.resume();

      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.11, now + 0.02);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
      master.connect(ctx.destination);

      const tones: Array<{ freq: number; start: number; dur: number }> = [
        { freq: 880, start: 0, dur: 0.12 },
        { freq: 1174.7, start: 0.1, dur: 0.22 }
      ];

      for (const tone of tones) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(tone.freq, now + tone.start);
        gain.gain.setValueAtTime(0.0001, now + tone.start);
        gain.gain.exponentialRampToValueAtTime(1, now + tone.start + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + tone.start + tone.dur);
        osc.connect(gain);
        gain.connect(master);
        osc.start(now + tone.start);
        osc.stop(now + tone.start + tone.dur + 0.02);
      }
    } catch {
      // Audio is optional
    }
  }
}
